import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RoseMark } from "@/components/RoseMark";
import { PrefSwitches, Disclosure } from "@/components/security/AiChoicesCard";

/** Signup consent step: nothing AI-written is generated for a new client until this is saved. */
export default function AiConsent() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({ self: true, clinician: true });
  const [disclosure, setDisclosure] = useState(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role !== "client") navigate("/clinician", { replace: true });
    api.get("/me/ai-preferences").then((r) => setDisclosure(r.data.disclosure)).catch(() => {});
  }, [user, navigate]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/me/ai-preferences/confirm", prefs);
      setUser((u) => (u ? { ...u, ai_prefs: prefs, ai_prefs_confirmed: true } : u));
      toast.success(prefs.self || prefs.clinician ? "Saved. You can change these any time in Security & Privacy." : "Saved — AI is off for your account.");
      navigate("/client", { replace: true });
    } catch { toast.error("Couldn't save your choices."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-start justify-center p-4 sm:p-8" data-testid="ai-consent-page">
      <div className="warm-card w-full max-w-2xl p-6 sm:p-8 space-y-6">
        <div className="flex items-center gap-3">
          <RoseMark className="w-10 h-10" />
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-800"><Bot className="w-4 h-4" /> One more choice before we start</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 mt-1">How should AI be used with your account?</h1>
          </div>
        </div>
        <p className="text-sm text-stone-600">Root & Rose can write personalised rewards and gentle insights using an AI model that only ever sees de-identified signals. Nothing AI-written is created until you choose here. Both are on by default; you can change them any time under Security &amp; Privacy.</p>
        <PrefSwitches prefs={prefs} onChange={(k, v) => setPrefs((p) => ({ ...p, [k]: v }))} />
        {disclosure && <Disclosure disclosure={disclosure} expanded={more} onToggle={() => setMore((v) => !v)} />}
        <button onClick={save} disabled={busy} className="amber-btn w-full" data-testid="ai-consent-continue">{busy ? "Saving…" : "Continue to my garden"}</button>
      </div>
    </div>
  );
}
